import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Heart, Building2, Briefcase, Brain, Shield, Check, ArrowRight, Star, Target, MessageCircle, Users, TrendingUp, Award, Sparkles, FileText, Lock, Phone, Download, X, ChevronRight, Eye, FlaskConical } from 'lucide-react';
import TrustedBy from '../components/TrustedBy';
import PageMeta from '../components/PageMeta';
import PlanComparisonTable from '../components/billing/PlanComparisonTable';
import NewsletterModal from '../components/NewsletterModal';
import WelcomePackImageReader from '../components/WelcomePackImageReader';
import { useWelcomePackGate } from '../components/WelcomePackGate';
import EarlyAccessModal from '../components/EarlyAccessModal';
import SiteNav from '../components/SiteNav';
import MeetTheAvAs from '../components/MeetTheAvAs';
import LanguageSuggestionBanner from '../components/LanguageSuggestionBanner';
import { useLanguage } from '../contexts/LanguageContext';

// Image-based welcome pack pages, served from /api/static/charters/.
// Using JPEGs (instead of PDFs) sidesteps every Cloudflare/MIME/PDF.js
// issue — plain <img> tags work everywhere.
const WELCOME_PACK_IMAGES = {
  parent: ['/api/static/charters/HiBFF-Parent-Compact.jpeg'],
  teen: [
    '/api/static/charters/HiBFF-Teen-Digital-Compact-1.jpeg',
    '/api/static/charters/HiBFF-Teen-Digital-Compact-2.jpeg',
    '/api/static/charters/HiBFF-Teen-Digital-Compact-3.jpeg',
    '/api/static/charters/HiBFF-Teen-Digital-Compact-4.jpeg',
    '/api/static/charters/HiBFF-Teen-Digital-Compact-5.jpeg',
    '/api/static/charters/HiBFF-Teen-Digital-Compact-6.jpeg',
    '/api/static/charters/HiBFF-Teen-Digital-Compact-7.jpeg',
    '/api/static/charters/HiBFF-Teen-Digital-Compact-8.jpeg',
  ],
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phase 1 spec — testimonials are scattered between sections, never in blocks.
const Testimonial = ({ id, quote, name, role, accent = '#F5831F' }) => (
  <div className="py-10 px-4 bg-white">
    <div className="max-w-2xl mx-auto text-center scroll-animate" data-testid={`testimonial-${id}`}>
      <p className="text-base sm:text-lg italic leading-relaxed" style={{ fontFamily: 'Georgia, serif', color: '#3F3A33' }}>
        &ldquo;{quote}&rdquo;
      </p>
      <p className="mt-3 text-sm text-gray-500">
        <span className="font-semibold not-italic" style={{ color: accent }}>— {name}</span>, {role}
      </p>
    </div>
  </div>
);

const HOMEPAGE_META = {
  '/': {
    title: 'HiBFF — Practice real conversations. Build real confidence.',
    description: "The world changed after Covid. An entire generation lost the years when social confidence forms naturally. We're giving it back — one conversation at a time. Safe. Private. Free to start.",
  },
  '/how-it-works': {
    title: 'How HiBFF Works — AI Conversation Practice for Teens',
    description: 'See how teens practise real conversations with safety-checked AI tutors, take on real-world challenges and build social confidence — with parents approving every account.',
  },
  '/safety': {
    title: 'Safety at HiBFF — Built for Teens, Approved by Parents',
    description: "Every HiBFF chat runs through an always-on safety layer with instant human handover in a crisis. Parents approve each account before chat unlocks. See how it works.",
  },
  '/teens-parents': {
    title: 'HiBFF for Teens & Parents — The Family Journey',
    description: 'The teen & family journey: parents approve the account, teens practise real conversations with safety-checked AI tutors and build social confidence step by step.',
  },
  '/adults': {
    title: 'HiBFF for Adults — Private Conversation Practice for Grown-Up Life',
    description: 'Join directly — no parent needed. Personalise your tutors, create your own AvA friends and practise interviews, dating and difficult conversations. Challenges are optional.',
  },
};

const Homepage = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [audience, setAudience] = useState('parent');
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [stickyBannerExpanded, setStickyBannerExpanded] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [pricingView, setPricingView] = useState('families');
  // Global audience persona — drives banner copy, 3 Ways to Grow track,
  // and the hero fork. Persisted to localStorage so a refresh keeps it.
  const [audiencePersona, setAudiencePersonaInternal] = useState(() => {
    try { return localStorage.getItem('hibff_signup_persona') === 'adult' ? 'adult' : 'teen'; }
    catch (e) { return 'teen'; }
  });
  const setAudiencePersona = (next) => {
    setAudiencePersonaInternal(next);
    try { localStorage.setItem('hibff_signup_persona', next); } catch (e) { /* */ }
  };
  const tracksAudience = audiencePersona;
  const setTracksAudience = setAudiencePersona;
  const [showCharterModal, setShowCharterModal] = useState(false);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [charterForm, setCharterForm] = useState({ fullName: '', teenName: '', email: '' });
  const [charterSubmitting, setCharterSubmitting] = useState(false);
  const [charterError, setCharterError] = useState('');
  const { t, dir } = useLanguage();
  const heroRef = useRef(null);

  // Lead-capture gate for the Welcome Pack — visitors must enter
  // name + email + role before any read / open / download action runs.
  // Persistent unlock lives in localStorage so they're never re-asked.
  const { requireUnlock, GateModal } = useWelcomePackGate();

  // Early-access modal — opened from the top "Limited Early Access" banner
  // and the inline "Reserve 90 days free" badge in the Welcome Pack section.
  const [showEarlyAccess, setShowEarlyAccess] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Clean-URL section routes (/how-it-works, /why-hibff, /safety) + legacy
  // /#hash links: scroll to the section on load and strip any # fragment.
  useEffect(() => {
    const pathMap = { '/how-it-works': 'how-it-works', '/why-hibff': 'founder-story', '/safety': 'safety', '/teens-parents': 'how-it-works', '/adults': 'adults' };
    const hashMap = { '#how-it-works': '/how-it-works', '#why-hibff': '/why-hibff', '#safety': '/safety', '#pricing': '/pricing', '#founder-story': '/why-hibff', '#adults': '/adults' };
    let target = pathMap[window.location.pathname];
    const hash = window.location.hash;
    if (!target && hash && hashMap[hash]) {
      target = pathMap[hashMap[hash]] || hash.slice(1);
      window.history.replaceState({}, '', hashMap[hash]);
    }
    if (!target) return;
    const tmr = setTimeout(() => {
      const el = document.getElementById(target);
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - 140;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 350);
    return () => clearTimeout(tmr);
  }, []);

  // Sticky nav + scroll-to-top visibility
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom;
        setShowStickyNav(heroBottom < -100);
      }
      setShowScrollTop(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Sticky banner auto-collapse: when the banner first appears, expand it for
  // 2 seconds so the user sees the message, then collapse it to a small icon.
  // The user can re-open it any time by clicking the icon.
  useEffect(() => {
    if (showStickyNav) {
      setStickyBannerExpanded(true);
      const t = setTimeout(() => setStickyBannerExpanded(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showStickyNav]);

  // Re-expand the sticky banner whenever the audience persona changes so the
  // user *sees* the copy swap (Teen → Adult and vice versa). Skip the very
  // first render so this doesn't fight the initial 2-second show timer above.
  const personaFirstRenderRef = useRef(true);
  useEffect(() => {
    if (personaFirstRenderRef.current) { personaFirstRenderRef.current = false; return undefined; }
    if (!showStickyNav) return undefined;
    setStickyBannerExpanded(true);
    const t = setTimeout(() => setStickyBannerExpanded(false), 2200);
    return () => clearTimeout(t);
  }, [audiencePersona, showStickyNav]);

  // Scroll animation observer (fires immediately on mount — prevents flash by
  // checking viewport state synchronously before the first browser paint
  // for elements already in view).
  useEffect(() => {
    const els = document.querySelectorAll('.scroll-animate');

    // Pre-flight: anything already in the viewport on mount reveals instantly
    // (no opacity-0 flash while observer warms up)
    els.forEach(el => {
      const rect = el.getBoundingClientRect();
      const inView = rect.top < window.innerHeight * 0.92 && rect.bottom > 0;
      if (inView) el.classList.add('animate-in');
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  }, []);

  const stickyCtaConfig = {
    parent: { label: 'Start Free as a Family', style: 'bg-[#F5831F] text-white hover:bg-[#E07318]' },
    school: { label: 'Book a School Demo', style: 'bg-[#7B4FD8] text-white hover:bg-[#6A3FC7]' },
    professional: { label: 'Book a Business Demo', style: 'bg-[#2EAA5C] text-white hover:bg-[#259A50]' },
  };
  const stickyCta = stickyCtaConfig[audience] || stickyCtaConfig.parent;

  const [charterSuccess, setCharterSuccess] = useState(false);

  const handleCharterDownload = async (e) => {
    e.preventDefault();
    setCharterError('');
    setCharterSuccess(false);
    const name = charterForm.fullName.trim();
    const teenName = charterForm.teenName.trim();
    const email = charterForm.email.trim();
    if (!name || !teenName || !email) { setCharterError('Please fill in all fields.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setCharterError('Please enter a valid email address.'); return; }
    setCharterSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/family/charter/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parent_name: name, teen_name: teenName, email }),
      });
      const data = await res.json();
      if (!res.ok) { setCharterError(data.detail || 'Something went wrong.'); return; }

      // Also add to waitlist
      fetch(`${API_URL}/api/waitlist/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, first_name: name, interest: 'parent', source: 'charter_download' }),
      }).catch(() => {});

      // Trigger download
      if (data.pdf_url) {
        const link = document.createElement('a');
        link.href = `${API_URL}${data.pdf_url}`;
        link.download = 'HiBFF_Commitment_Charter.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setCharterSuccess(true);
    } catch {
      setCharterError('Something went wrong. Please try again.');
    } finally {
      setCharterSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" dir={dir}>
      <LanguageSuggestionBanner />
      <PageMeta
        slug="home"
        path={HOMEPAGE_META[pathname] ? pathname : '/'}
        canonicalPath="/"
        {...(HOMEPAGE_META[pathname] || HOMEPAGE_META['/'])}
      />

      {/* ── Section 01: Navigation Bar (canonical SiteNav) ── */}
      <SiteNav current="home" mode="fixed" />

      {/* ── Section 13: Sticky Promo (appears on scroll) ──
          - Expanded for 2 seconds when it first appears so the user sees the message
          - Then collapses to a small clickable pill in the top-right corner
          - Clicking the pill re-expands it; clicking "Join Free" navigates to register */}
      {showStickyNav && (
        <>
          {/* Expanded banner — persona-aware copy */}
          <div
            className={`fixed top-0 inset-x-0 z-50 bg-gradient-to-r from-[#1A1209] via-[#2D1F10] to-[#1A1209] shadow-lg transition-all duration-500 ${stickyBannerExpanded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'}`}
            data-testid="sticky-nav"
          >
            <div className="max-w-6xl mx-auto px-4 h-[48px] flex items-center justify-center gap-3">
              {audiencePersona === 'adult' ? (
                <span className="text-xs sm:text-sm font-semibold text-white/90 text-center" data-testid="sticky-banner-text">
                  <span className="text-[#F5831F] font-bold">🚀 Professional Track</span> · Practise your next high-stakes conversation risk-free
                </span>
              ) : (
                <span className="text-xs sm:text-sm font-semibold text-white/90 text-center" data-testid="sticky-banner-text">
                  <span className="text-[#F5831F] font-bold">Parents — HiBFF is open</span> · Start free today, no card needed
                </span>
              )}
              <button
                onClick={() => navigate(`/register?plan=free&utm_source=sticky_banner&persona=${audiencePersona}`)}
                className="px-4 py-1.5 text-xs font-bold text-[#1A1209] bg-[#F5831F] rounded-full hover:bg-[#FF9A3C] transition-all whitespace-nowrap"
                data-testid="sticky-promo-btn"
              >
                Start Free
              </button>
              <button
                onClick={() => setStickyBannerExpanded(false)}
                className="ml-1 p-1 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Collapse promo banner"
                data-testid="sticky-promo-close-btn"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Collapsed pill — stays visible after the banner auto-hides */}
          <button
            onClick={() => setStickyBannerExpanded(true)}
            className={`fixed top-3 right-3 z-50 flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-[#1A1209] text-white shadow-lg hover:bg-[#2D1F10] hover:scale-105 transition-all duration-300 ${!stickyBannerExpanded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}
            aria-label="Show start-free promo"
            data-testid="sticky-promo-pill"
          >
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#F5831F]">
              <Sparkles size={12} className="text-[#1A1209]" />
            </span>
            <span className="text-xs font-semibold whitespace-nowrap">Start Free</span>
          </button>
        </>
      )}

      {/* ── Section 02: The Hero ── */}
      <section ref={heroRef} className="pt-24 sm:pt-32 lg:pt-56 pb-8 px-4 relative overflow-hidden">
        {/* Subtle warm gradient background */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(245,131,31,0.06) 0%, rgba(123,79,216,0.04) 40%, transparent 70%)' }} />
        <div className="max-w-6xl mx-auto lg:translate-x-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Copy */}
            <div className="text-center lg:text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                <span className="text-gray-900">{t('hero.h1a')}</span><br />
                <span style={{ color: '#7B4FD8' }}>{t('hero.h1b')}</span>
              </h1>
              <p className="text-lg mb-8" style={{ fontFamily: 'Georgia, serif', color: '#4A4A4A' }}>
                An entire generation lost the years when social confidence forms naturally. Practice real conversations with AI AvA&apos;s that feels <span className="whitespace-nowrap"><strong className="text-gray-900">Safe</strong> and <strong className="text-gray-900">Private</strong></span>
              </p>
              <p className="text-sm mb-8 -mt-4 max-w-lg mx-auto lg:mx-0 text-gray-800 font-bold" data-testid="hero-safety-line">
                Every conversation is personality-matched to your teen and protected by multi-layer{' '}
                <a href="/safety" className="text-purple-700 underline decoration-2 underline-offset-2 hover:text-purple-900" data-testid="hero-safety-link">safety</a>
                {' '}guardrails.
              </p>
              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-3 mb-4">
                <button onClick={() => navigate('/register')} className="px-8 py-4 bg-[#7B4FD8] text-white font-semibold rounded-xl hover:bg-[#6A3FC7] shadow-lg hover:shadow-xl transition-all flex items-center gap-2 text-lg" data-testid="hero-cta-btn">
                  {t('hero.cta')} <ArrowRight size={20} />
                </button>
                <a href="https://play.google.com/store/apps/details?id=com.hibff.coach" target="_blank" rel="noopener noreferrer" aria-label="Get HiBFF on Google Play" data-testid="hero-google-play-badge" className="inline-flex items-center">
                  <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" className="h-[58px] w-auto" loading="lazy" />
                </a>
              </div>
              <p className="text-sm font-bold text-gray-900 flex items-center justify-center lg:justify-start gap-1.5" data-testid="hero-data-pledge">
                <Shield size={15} style={{ color: '#059669' }} />
                Your data belongs to you. No advertising.{' '}
                <a href="/privacy" className="text-purple-700 underline decoration-2 underline-offset-2 hover:text-purple-900 font-semibold" data-testid="hero-data-pledge-link">Our policy</a>
              </p>
            </div>

            {/* Right: Product Mockup — visible on all viewports */}
            <div className="flex justify-center mt-8 lg:mt-0 float-anim" data-testid="hero-mockup">
              <div className="relative w-[280px] sm:w-[320px]">
                {/* Phone frame */}
                <div className="rounded-[2rem] p-3 shadow-2xl" style={{ backgroundColor: '#1A1209', border: '1px solid #3D2E1A' }}>
                  <div className="rounded-[1.4rem] overflow-hidden bg-white">
                    {/* Status bar */}
                    <div className="px-4 py-2 flex items-center justify-between" style={{ backgroundColor: '#F8F7F4' }}>
                      <span className="text-[10px] font-semibold text-gray-800">9:41</span>
                      <span className="text-[10px] font-bold" style={{ color: '#F5831F' }}>HiBFF</span>
                      <div className="flex gap-0.5">
                        <div className="w-3 h-1.5 rounded-sm bg-gray-400" />
                        <div className="w-1 h-1.5 rounded-sm bg-gray-300" />
                      </div>
                    </div>
                    {/* Chat header — Neo avatar with breathing animation + speaking indicator */}
                    <div className="px-4 py-4 flex items-center gap-3 border-b border-gray-100">
                      <div className="relative">
                        {/* Pulsing ring (speaking indicator) */}
                        <div className="absolute inset-0 rounded-full opacity-60" style={{
                          animation: 'avapulse 2s ease-in-out infinite',
                          background: 'radial-gradient(circle, rgba(245,131,31,0.4) 0%, transparent 70%)',
                          transform: 'scale(1.4)',
                        }} />
                        <img src="/avas/neo.jpg" alt="Neo" className="relative w-16 h-16 rounded-full object-cover shadow-md" style={{ border: '3px solid #F5831F', animation: 'avabreathe 4s ease-in-out infinite' }} />
                        {/* Online dot */}
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white" style={{ backgroundColor: '#10B981' }} />
                      </div>
                      <div>
                        <p className="text-base font-bold text-gray-900" data-testid="mockup-ava-name">Neo</p>
                        <p className="text-xs flex items-center gap-1" style={{ color: '#10B981' }}>
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" style={{ animation: 'avapulse 1.5s ease-in-out infinite' }} />
                          Your Friend
                        </p>
                      </div>
                    </div>
                    {/* Chat messages — Neo conversation (Phase 1 spec) */}
                    <div className="px-4 pt-5 pb-4 space-y-3" style={{ minHeight: '220px' }} data-testid="mockup-neo-convo">
                      <div className="flex justify-start">
                        <div className="px-3 py-2 rounded-2xl rounded-bl-md text-[13px] max-w-[85%]" style={{ backgroundColor: '#F8F7F4', color: '#333' }}>
                          Alex! You made it to Challenge 10. That&apos;s huge. 🎉 How are you feeling? Nervous? Excited? Both?
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <div className="px-3 py-2 rounded-2xl rounded-br-md text-[13px] max-w-[85%] text-white" style={{ backgroundColor: '#7B4FD8' }}>
                          A bit nervous, actually. But ready.
                        </div>
                      </div>
                      <div className="flex justify-start">
                        <div className="px-3 py-2 rounded-2xl rounded-bl-md text-[13px] max-w-[85%]" style={{ backgroundColor: '#F8F7F4', color: '#333' }}>
                          That&apos;s exactly how you&apos;re supposed to feel. Nervous means you care. And ready means you&apos;ve got this. These next challenges unlock something special. Real rewards. Your Vault. Let&apos;s do this together.
                        </div>
                      </div>
                    </div>
                    {/* Input bar */}
                    <div className="px-4 py-3 border-t border-gray-100 flex items-center gap-2">
                      <div className="flex-1 px-3 py-2 rounded-full text-xs" style={{ backgroundColor: '#F8F7F4', color: '#999' }}>Message Neo…</div>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#7B4FD8' }}>
                        <ArrowRight size={14} className="text-white" />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Floating badge — sits in the white space below the "Your AvA Team" header */}
                <div className="absolute -right-4 top-32 px-3 py-2 bg-white rounded-xl shadow-lg border border-gray-100 flex items-center gap-2" style={{ animation: 'float-gentle 5s ease-in-out infinite 1s' }}>
                  <Award size={16} style={{ color: '#F59E0B' }} />
                  <span className="text-xs font-bold text-gray-800">Neo responds with Text & Voice</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial — after hero (Phase 1 spec) */}
      <Testimonial
        id="sarah"
        quote="I was nervous about screen time. But HiBFF isn't more screen time — it's practice for real life. My daughter actually used what she learned with Neo in a group project. She came home and told me about it. That's never happened before."
        name="Sarah"
        role="Parent of a 15-year-old"
      />

      {/* Dashboard Showcase removed 20 Aug 2026 — screenshot contained internal
          test data (TestCorp E2E, MRR figures). Reinstate once a clean,
          production-safe composite image is available. */}

      {/* ── Section 03: Video + Founder Quote (locked — attribution only) ── */}
      <section className="pt-12 pb-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto scroll-animate">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
            <div className="md:ml-auto">
              <div className="rounded-2xl overflow-hidden shadow-xl max-w-sm mx-auto md:mx-0">
                <div style={{ position: 'relative', paddingTop: '177.78%' }}>
                  <iframe
                    src="https://iframe.mediadelivery.net/embed/621268/63921d6e-1d81-4fa7-89f7-aa2bcd3c66a6?autoplay=false&preload=true"
                    loading="lazy"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    title="HiBFF — Why, What & How"
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-4 text-center md:text-left">{t('quote.watch')}</p>
            </div>
            <div>
              <span className="text-6xl leading-none font-serif" style={{ color: '#F5831F' }}>&quot;</span>
              <p className="text-2xl font-normal leading-relaxed mt-2" style={{ fontFamily: 'Georgia, serif', color: '#1A1209' }}>
                {t('quote.text')}
              </p>
              <div className="flex items-center gap-3 mt-8">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: '#1A1209', color: '#F5831F' }}>RP</div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Robert Parson</p>
                  <p className="text-sm text-gray-600">{t('quote.role')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 04: The Founder Story ── */}
      <section id="founder-story" className="py-20 px-4" style={{ backgroundColor: '#FFF5EA', scrollMarginTop: '80px' }}>
        <div className="max-w-3xl mx-auto scroll-animate">
          <p className="text-center text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#F5831F' }}>{t('story.eyebrow')}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 text-center mb-8" style={{ fontFamily: 'Georgia, serif' }}>
            {t('story.h')}
          </h2>
          <div className="text-base leading-relaxed text-gray-700 space-y-4" style={{ fontFamily: 'Georgia, serif' }}>
            <p>{t('story.p1')}</p>
            <p>{t('story.p2')}</p>
            <p>{t('story.p3')}</p>
            <p>{t('story.p4')}</p>
          </div>
          <div className="mt-8">
            <p className="font-semibold text-gray-900">Robert Parson</p>
            <p className="text-sm text-gray-600">{t('quote.role')}</p>
          </div>
          {/* CTA after emotional peak */}
          <div className="mt-10 pt-8 border-t border-orange-200/60 flex flex-col sm:flex-row items-center gap-4" data-testid="founder-cta">
            <button onClick={() => navigate('/register')} className="px-6 py-3 bg-[#F5831F] text-white font-semibold rounded-xl hover:bg-[#E07318] shadow-md hover:shadow-lg transition-all flex items-center gap-2">
              {t('story.cta')} <ArrowRight size={18} />
            </button>
            <span className="text-sm text-gray-500">{t('story.note')}</span>
          </div>
        </div>
      </section>
      {/* ── Meet Your AvA Team (Phase 1 spec) ── */}
      <section id="ava-team" className="py-16 px-4 bg-white" style={{ scrollMarginTop: '80px' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 text-center mb-3 scroll-animate" style={{ fontFamily: 'Georgia, serif' }}>Meet Your AvA Team</h2>
          <p className="text-gray-600 text-center mb-12 scroll-animate delay-1">Nine specialist tutors. Nine parts of teen life. One mission: your confidence.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 scroll-animate delay-2" data-testid="ava-team-grid">
            {[
              { key: 'maya', name: 'Maya', role: 'Your Guide', img: '/clare-ava.png', tag: 'Fixed — deep knowledge', color: '#F5831F', desc: 'Maya sets everything up, explains how HiBFF works, and is always there in your dashboard. She knows the platform inside out.' },
              { key: 'neo', name: 'Neo', role: 'Your Friend', img: '/avas/neo.jpg', tag: 'Customisable — image, voice & name', color: '#7B4FD8', desc: 'Your customisable challenge tutor. Your teen picks Neo\u2019s image, voice, name and backstory — a friend that feels like home.' },
              { key: 'rebecca', name: 'Rebecca', role: 'Your Tutor', img: '/avas/rebecca.png', tag: 'Science & Tech Tutor', color: '#2EAA5C', desc: 'Structured knowledge for school and beyond. Rebecca teaches Socratic-style — asking the questions that help your teen find the answer themselves.' },
              { key: 'leo', name: 'Leo', role: 'Relationship Communication', img: '/avas/leo.jpg', tag: 'Topic-locked', color: '#E11D48', desc: 'Dating, crushes & relationship communication — rehearse kindness, boundaries and difficult messages safely.' },
              { key: 'marcus', name: 'Marcus', role: 'Career & Authority', img: '/avas/marcus.jpg', tag: 'Topic-locked', color: '#1D4ED8', desc: 'Job interviews, university admissions and speaking up respectfully with teachers — rehearsed line by line.' },
              { key: 'chloe', name: 'Chloe', role: 'Social Dynamics & Conflict', img: '/avas/chloe.jpg', tag: 'Topic-locked', color: '#059669', desc: 'Peer dynamics, group projects and conflict resolution — practise standing your ground calmly and kindly.' },
              { key: 'ethan', name: 'Ethan', role: 'Public Speaking & Voice', img: '/avas/ethan.jpg', tag: 'Topic-locked', color: '#B45309', desc: 'Class presentations and voice confidence — pace, filler words, tone and structure, without the panic.' },
              { key: 'sarah', name: 'Sarah', role: 'Organization & Boundaries', img: '/avas/sarah.jpg', tag: 'Topic-locked', color: '#0D9488', desc: 'Workload, time management and beating procrastination — plus saying no and setting healthy boundaries.' },
              { key: 'petra', name: 'Petra', role: 'Digital Life & Online Safety', img: '/avas/petra.jpg', tag: 'Topic-locked', color: '#DB2777', desc: 'Digital literacy, cyberbullying and group chat drama — staying safe, kind and in control online.' },
            ].map(a => (
              <div key={a.key} className="p-6 rounded-2xl border-2 border-gray-100 hover:shadow-lg transition-shadow text-center bg-white" data-testid={`ava-card-${a.key}`}>
                <img src={a.img} alt={a.name} loading="lazy" decoding="async" className="w-20 h-20 rounded-full object-cover mx-auto mb-4 shadow-md" style={{ border: `3px solid ${a.color}` }} />
                <h3 className="text-lg font-bold text-gray-900">{a.name}</h3>
                <p className="text-sm font-semibold mb-2" style={{ color: a.color }}>{a.role}</p>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">{a.desc}</p>
                <span className="inline-block px-3 py-1 rounded-full text-[11px] font-bold" style={{ backgroundColor: `${a.color}18`, color: a.color }}>{a.tag}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-600 mt-8 max-w-2xl mx-auto" data-testid="specialist-tutor-library">Every specialist tutor is an expert in one high-stakes teen scenario — and nothing else. Fully customisable image, voice and name, always inside the same safety guardrails.</p>
          <p className="text-sm text-gray-600 text-center mt-8 max-w-2xl mx-auto scroll-animate" data-testid="ocean-calibration-line">
            Every AvA is calibrated by a friendly <strong className="text-gray-900">22-question OCEAN personality assessment</strong> — so conversations match how your teen actually thinks and feels.
          </p>
        </div>
      </section>

      {/* Testimonial — after AvA section (Phase 1 spec) */}
      <Testimonial
        id="alex"
        quote="I was so nervous about my first job interview. Elena — the interview tutor I personalised from HiBFF's library — helped me practise all the questions. When I walked in, I actually felt ready. I got the job."
        name="Alex"
        role="17"
        accent="#7B4FD8"
      />
      {/* ── Section 08: How It Works (5 steps, parent first) ── */}
      <section id="how-it-works" className="py-16 px-4 bg-gray-50" style={{ scrollMarginTop: '80px' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 text-center mb-4 scroll-animate">{t('how.h')}</h2>
          <p className="text-base sm:text-lg text-gray-600 text-center mb-12 scroll-animate delay-1">{t('how.sub')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { step: '1', title: t('how.1t'), desc: t('how.1d'), icon: Users },
              { step: '2', title: t('how.2t'), desc: t('how.2d'), icon: Target },
              { step: '3', title: t('how.3t'), desc: t('how.3d'), icon: MessageCircle },
              { step: '4', title: t('how.4t'), desc: t('how.4d'), icon: Sparkles },
              { step: '5', title: t('how.5t'), desc: t('how.5d'), icon: Award },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.step} className={`text-center scroll-animate step-connector`} style={{ transitionDelay: `${i * 0.1}s` }}>
                  <div className="relative inline-flex">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: 'linear-gradient(135deg, #EDE9FE, #DDD6FE)' }}>
                      <Icon size={24} className="text-purple-600" />
                    </div>
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white" style={{ backgroundColor: '#7B4FD8' }}>{s.step}</span>
                  </div>
                  <h3 className="mb-1" style={{ fontSize: '15px', fontWeight: 'bold', color: '#1A1A1A' }}>{s.title}</h3>
                  <p style={{ fontSize: '13px', lineHeight: '1.6', color: '#666666' }}>{s.desc}</p>
                  {i < 4 && <ChevronRight size={16} className="mx-auto mt-3 text-purple-300 lg:hidden" />}
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* ── HiBFF for Adults (Oct 2026 — Teens to Adults for life) ── */}
      <section id="adults" className="py-20 px-6 bg-[#1A1209]" data-testid="adults-section">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-amber-400 uppercase mb-3 scroll-animate">{t('adults.kicker')}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4 scroll-animate">{t('adults.h')}</h2>
          <p className="text-base sm:text-lg text-amber-50/70 mb-12 max-w-2xl scroll-animate delay-1">{t('adults.sub')}</p>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { title: t('adults.1t'), desc: t('adults.1d') },
              { title: t('adults.2t'), desc: t('adults.2d') },
              { title: t('adults.3t'), desc: t('adults.3d') },
            ].map((c, i) => (
              <div key={i} className="rounded-2xl p-6 bg-white/5 border border-white/10 scroll-animate" data-testid={`adults-tile-${i + 1}`}>
                <h3 className="text-lg font-bold text-white mb-2">{c.title}</h3>
                <p className="text-sm text-amber-50/60 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
          <a
            href="/adult"
            data-testid="adults-cta"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-amber-400 text-[#1A1209] font-bold text-sm hover:bg-amber-300 transition-colors"
          >
            {t('adults.cta')}
          </a>
          <p className="text-sm font-semibold text-white/90 flex items-center justify-center gap-1.5 mt-6" data-testid="adults-data-pledge">
            <Shield size={15} style={{ color: '#FCD34D' }} />
            Your data belongs to you. No advertising.{' '}
            <a href="/privacy" className="underline decoration-2 underline-offset-2 text-amber-300 hover:text-amber-200" data-testid="adults-data-pledge-link">Our policy</a>
          </p>
        </div>
      </section>
      {/* ── Section 10: The HiBFF Welcome Pack ── */}
      {/* Restored after rollback. Two PDFs hosted in /public/charter/ and
            downloaded together when the visitor clicks the primary button. */}
      <section className="py-20 px-4" style={{ backgroundColor: '#FFF5EA' }} id="welcome-pack">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center scroll-animate">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#F5831F' }}>{t('pack.eyebrow')}</p>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                {t('pack.h1')}<br />{t('pack.h2')}<br />{t('pack.h3')}
              </h2>
              <p className="text-base text-gray-700 leading-relaxed mb-8" style={{ fontFamily: 'Georgia, serif' }}>
                {t('pack.p')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    requireUnlock(() => {
                      // Sequentially trigger downloads of both PDFs so the
                      // visitor receives the complete "Welcome Pack" with one click.
                      const files = [
                        { url: '/api/static/charters/HiBFF-Parent-Compact.pdf', name: 'HiBFF Parent Compact.pdf' },
                        { url: '/api/static/charters/HiBFF-Teen-Digital-Compact.pdf', name: 'HiBFF Teen Digital Compact.pdf' },
                      ];
                      files.forEach((f, i) => {
                        setTimeout(() => {
                          const a = document.createElement('a');
                          a.href = f.url;
                          a.download = f.name;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        }, i * 400);
                      });
                    }, 'download');
                  }}
                  className="px-7 py-3.5 rounded-xl font-semibold text-base border-2 hover:bg-orange-50 transition-colors flex items-center gap-2"
                  style={{ borderColor: '#F5831F', color: '#F5831F' }}
                  data-testid="welcome-pack-download-btn"
                >
                  <Download size={18} /> {t('pack.download')}
                </button>
                <button
                  onClick={() => requireUnlock(() => setShowCharterModal('parent'), 'read')}
                  className="px-7 py-3.5 rounded-xl font-semibold text-base bg-[#F5831F] text-white hover:bg-[#E07318] transition-colors flex items-center gap-2"
                  data-testid="welcome-pack-read-btn"
                >
                  <Eye size={18} /> {t('pack.read')}
                </button>
              </div>
              {/* Start-free CTA — directly to the Free plan signup. We
                  no longer route through a waitlist modal since the Free
                  plan is live and available worldwide. */}
              <button
                type="button"
                onClick={() => navigate('/register?plan=free&utm_source=welcome_pack')}
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold border border-[#F5831F]/40 bg-[#FFF5EA] text-[#A65812] hover:bg-[#FFE9D1] transition-colors"
                data-testid="welcome-pack-start-free-badge"
              >
                <Sparkles size={14} style={{ color: '#F5831F' }} />
                {t('pack.free')}
                <ArrowRight size={12} />
              </button>
            </div>
            <div className="flex items-center justify-center">
              {/* Two stacked document cards — subtle rotation suggests a pack */}
              <div className="relative">
                <div className="w-72 h-96 bg-white rounded-xl shadow-lg border-t-4 border-orange-400 border-l border-r border-b border-gray-200 flex items-center justify-center p-8 absolute -left-4 top-4 transform -rotate-3" aria-hidden="true">
                  <div className="text-center opacity-70">
                    <img src="/hibff-logo.png" alt="" loading="lazy" decoding="async" className="h-10 mx-auto mb-4 opacity-60" />
                    <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>A Welcome Letter — The Parent Compact</p>
                  </div>
                </div>
                <div className="w-72 h-96 bg-white rounded-xl shadow-xl border-t-4 border-orange-500 border-l border-r border-b border-gray-200 flex items-center justify-center p-8 relative transform rotate-2">
                  <div className="text-center">
                    <img src="/hibff-logo.png" alt="HiBFF" loading="lazy" decoding="async" className="h-12 mx-auto mb-4 opacity-60" />
                    <p className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Georgia, serif' }}>The HiBFF Commitment Charter</p>
                    <div className="border-t border-gray-200 my-3" />
                    <p className="text-xs text-gray-500 leading-relaxed">Your practice is private. I will not ask to read your chats. The rewards you earn belong to you. I am focused on your consistency and courage, not grades.</p>
                    <div className="mt-6 border-b border-gray-300 w-32 mx-auto" />
                    <p className="text-[10px] text-gray-400 mt-1">Parent digital signature</p>
                    <div className="mt-4 border-b border-gray-300 w-32 mx-auto" />
                    <p className="text-[10px] text-gray-400 mt-1">Teen digital signature</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 11: Built for Safety (with Phase 1 highlight box) ── */}
      <section id="safety" className="py-16 px-4" style={{ scrollMarginTop: '80px' }}>
        <div className="max-w-4xl mx-auto rounded-3xl border-2 p-6 sm:p-10" style={{ borderColor: '#F5831F55', background: 'linear-gradient(180deg, #FFFDF9 0%, #FFFFFF 100%)' }}>
          <div className="mb-8 px-5 py-4 rounded-2xl text-center scroll-animate" style={{ backgroundColor: '#FFF5EA', border: '1px solid #F5831F55' }} data-testid="safety-highlight-box">
            <p className="text-lg sm:text-xl font-bold" style={{ color: '#1A1209', fontFamily: 'Georgia, serif' }}>🛡️ Safety Isn&apos;t an Add-On. It&apos;s the Foundation.</p>
            <p className="text-sm text-gray-700 mt-1">Every design decision puts your teenager&apos;s growth — and safety — first.</p>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 text-center mb-3 scroll-animate">{t('safety.h')}</h2>
          <p className="text-gray-600 text-center mb-10 scroll-animate delay-1">{t('safety.sub')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 scroll-animate delay-2">
            {[
              { icon: Shield, title: t('safety.1t'), desc: t('safety.1d') },
              { icon: Check, title: t('safety.2t'), desc: t('safety.2d') },
              { icon: Target, title: t('safety.3t'), desc: t('safety.3d') },
              { icon: Users, title: t('safety.4t'), desc: t('safety.4d') },
              { icon: Award, title: t('safety.5t'), desc: t('safety.5d') },
              { icon: TrendingUp, title: t('safety.6t'), desc: t('safety.6d') },
              { icon: Shield, title: t('safety.7t'), desc: t('safety.7d') },
              { icon: Brain, title: t('safety.8t'), desc: t('safety.8d') },
              { icon: Building2, title: t('safety.9t'), desc: t('safety.9d') },
            ].map(f => {
              const Icon = f.icon;
              return <div key={f.title} className="p-4 bg-gray-50 rounded-xl"><Icon size={20} className="text-purple-500 mb-2" /><h3 className="font-bold text-gray-900 text-sm mb-1">{f.title}</h3><p className="text-xs text-gray-600">{f.desc}</p></div>;
            })}
          </div>

          {/* Human Escalation Safety Net — the AI knows its limits */}
          <div className="mt-8 p-6 rounded-2xl border-2" style={{ borderColor: '#7B4FD855', background: 'linear-gradient(180deg, #FBFAFE 0%, #FFFFFF 100%)' }} data-testid="escalation-safety-net">
            <h3 className="text-lg font-bold text-gray-900 mb-3" style={{ fontFamily: 'Georgia, serif' }}>🤝 The Human Escalation Safety Net</h3>
            <ul className="space-y-2.5 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <Shield size={16} className="text-purple-500 flex-shrink-0 mt-0.5" />
                <span><strong className="text-gray-900">Multi-layer guardrails</strong> — NVIDIA NeMo Guardrails plus a custom Claude safety wrapper keep every conversation strictly on-challenge.</span>
              </li>
              <li className="flex items-start gap-2">
                <Heart size={16} className="text-rose-500 flex-shrink-0 mt-0.5" />
                <span><strong className="text-gray-900">Live sentiment monitoring</strong> — the system detects emotional shifts in real time, not just banned words.</span>
              </li>
              <li className="flex items-start gap-2">
                <Users size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <span><strong className="text-gray-900">Seamless human handover</strong> — if a teen needs more than an AI can give, a guided 3-step handover connects them to real human support with a personal introduction. The AI knows its limits.</span>
              </li>
            </ul>
          </div>

          {/* Parents Can See / Cannot See — trust without surveillance */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 scroll-animate" data-testid="parent-visibility-table">
            <div className="p-5 rounded-2xl border border-emerald-200" style={{ backgroundColor: '#F2FBF6' }}>
              <h3 className="text-sm font-bold mb-3" style={{ color: '#0F8A4B' }}>✅ Parents Can See</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2"><Check size={14} className="text-green-500 flex-shrink-0" /> Challenges completed</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-green-500 flex-shrink-0" /> Badges earned</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-green-500 flex-shrink-0" /> Progress over time</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-green-500 flex-shrink-0" /> Milestones reached</li>
              </ul>
            </div>
            <div className="p-5 rounded-2xl border border-rose-200" style={{ backgroundColor: '#FDF4F5' }}>
              <h3 className="text-sm font-bold mb-3" style={{ color: '#BE123C' }}>❌ Parents Cannot See</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2"><X size={14} className="text-rose-400 flex-shrink-0" /> Conversation content</li>
                <li className="flex items-center gap-2"><X size={14} className="text-rose-400 flex-shrink-0" /> Private chats</li>
                <li className="flex items-center gap-2"><X size={14} className="text-rose-400 flex-shrink-0" /> Deleted messages</li>
                <li className="flex items-center gap-2"><X size={14} className="text-rose-400 flex-shrink-0" /> AI practice sessions</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial — after safety (Phase 1 spec) */}
      <Testimonial
        id="emma"
        quote="My 16-year-old has always struggled with confidence. After 8 challenges with Neo, I saw something shift. He started contributing in class. He joined a club. He said, 'Neo helped me practise what to say.' That's worth everything."
        name="Emma"
        role="Parent of a 16-year-old"
        accent="#2EAA5C"
      />

      {/* ── Section 12: Pricing (with audience toggle) ── */}
      <section id="pricing" className="py-16 px-4 bg-gray-50" style={{ scrollMarginTop: '80px' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 text-center scroll-animate" style={{ marginBottom: '32px' }}>{t('price.h')}</h2>
          <p className="text-gray-600 text-center scroll-animate delay-1" style={{ marginBottom: '24px' }}>{t('price.sub')}</p>
          <div className="text-center scroll-animate delay-2" style={{ marginBottom: '48px' }} data-testid="pricing-brand-line">
            <p className="text-base font-semibold" style={{ fontFamily: 'Georgia, serif', color: '#1A1209' }}>HiBFF — Hi Brave Friends Forever</p>
            <p className="text-sm italic mt-1 max-w-2xl mx-auto" style={{ fontFamily: 'Georgia, serif', color: '#7B4FD8' }}>
              Brave takes courage. Courage builds confidence. Confidence builds character.
              <br />
              And character is the foundation of friends forever.
            </p>
          </div>

          {/* Toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-gray-200 rounded-lg p-1 flex-wrap justify-center" data-testid="pricing-view-toggle">
              {[
                { id: 'families',   label: t('price.tabFam'), persona: 'teen' },
                { id: 'adults',     label: t('price.tabAdult'),     persona: 'adult' },
                { id: 'schools',    label: t('price.tabSchools') },
                { id: 'businesses', label: t('price.tabBiz') },
              ].map(t => (
                <button key={t.id} onClick={() => { setPricingView(t.id); if (t.persona) setAudiencePersona(t.persona); }}
                  data-testid={`pricing-tab-${t.id}`}
                  className={`px-4 sm:px-5 min-h-[44px] rounded-md text-sm font-medium transition-colors ${pricingView === t.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Families pricing — Free → Starter → Plus → Annual */}
          {pricingView === 'families' && (
            <>
              {/* Multi-teen family discount banner — narrow, centered, sits
                  directly beneath the Families/Schools/Businesses toggle so
                  it reads as a sub-headline for the Families view. */}
              <div className="-mt-6 mb-10 max-w-md mx-auto px-4 py-3 bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 border border-amber-200 rounded-xl text-center" data-testid="family-discount-banner">
                <p className="text-sm font-bold text-gray-900 inline-flex items-center justify-center gap-1.5">
                  <Users size={14} style={{ color: '#F5831F' }} />
                  {t('price.famBanner1')}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Every additional teen 40% off, all on one bill
                  <br />
                  — exclusive to the <strong className="font-bold text-gray-900">Brave</strong> annual plan.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4" style={{ gap: '20px' }}>
              {[
                { name: 'First Steps',       tagline: 'A taste of HiBFF',                 price: '£0',   period: '/forever', badgeLabel: 'FIRST STEPS',      badgeStyle: 'bg-emerald-100 text-emerald-700 border border-emerald-300', features: ['Meet 3 of our 9 AvAs — Rebecca (Knowledge Tutor), Chloe (Friendships) & Marcus (Careers & School)', '10 FREE voice minutes — just enough to experience the magic', 'Pay as you go — top up Heart time credits with your own card, whenever you need them (challenges earn virtual badges only)'], cta: 'Start Free — No Card',    ctaStyle: 'bg-emerald-600 text-white hover:bg-emerald-700', free: true },
                { name: 'Steady',  tagline: 'One teen, building confidence',         price: '£16',  period: '/mo',     badgeLabel: 'STEADY', badgeStyle: 'bg-purple-100 text-purple-700 border border-purple-300', features: ['6 of the 9 AvAs to practise with', 'Neo — Your Friend (customise name, voice, image, and backstory)', 'Challenges 1-10 (Virtual Badges)', 'Unlimited text messages', '45 voice minutes / month', 'Confidence tracking + insights'], excluded: ['Challenges 11-20', 'Achievement NFTs', 'Parent/Teen shared access'], cta: 'Upgrade to Steady', ctaStyle: 'bg-purple-50 text-[#7B4FD8] border-2 border-[#7B4FD8] hover:bg-purple-100' },
                { name: 'Together',   tagline: 'Families & growth',   price: '£28',  period: '/mo',     badgeLabel: 'TOGETHER',  badgeStyle: 'bg-[#7B4FD8] text-white',                                  features: ['All 9 AvAs — the complete tutor team', 'Challenges 1-20 (Virtual Badges + NFT Achievements at #10)', 'Unlimited text', '180 voice minutes / month', 'Parent/Teen shared (up to 2 teens)', 'AI-generated rewards in the 180-Day Vault (parent-authorised)', '30-day refund + 90-day pause + indefinite pause options'],                                                                                  cta: 'Upgrade to Together', ctaStyle: 'bg-[#7B4FD8] text-white hover:bg-[#6A3FC7]', popular: true },
                { name: 'Brave',      tagline: 'Full commitment', price: '£188', period: '/year',   badgeLabel: 'BRAVE ★ BEST VALUE', badgeStyle: 'bg-[#F5831F] text-white',                                  features: ['Every AvA + early access', 'All challenges + early access', '300 voice minutes / month (120 bonus vs monthly)', 'Everything in Together', 'Priority support', 'Same refund / pause options'],                                                                                                                                                cta: 'Upgrade to Brave',    ctaStyle: 'bg-[#F5831F] text-white hover:bg-[#E07318]', annual: true },
              ].map(plan => (
                <div key={plan.name} className={`p-5 md:p-6 bg-white rounded-2xl border-2 ${plan.free ? 'border-emerald-400 shadow-lg' : plan.popular ? 'border-purple-400 shadow-lg' : plan.annual ? 'border-amber-300' : 'border-gray-200'} relative flex flex-col`} data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  {/* Bigger, taller, more legible plan badge */}
                  <span className={`absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 text-sm font-extrabold tracking-[0.18em] rounded-full whitespace-nowrap shadow-sm ${plan.badgeStyle}`} data-testid={`badge-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}>{plan.badgeLabel}</span>

                  {/* Tagline — centered, fixed 2-line space so prices align */}
                  <h3 className="font-bold text-gray-800 mt-4 text-center text-lg leading-snug min-h-[3.25rem] flex items-center justify-center px-2" style={{ fontFamily: 'Georgia, serif' }}>{plan.tagline}</h3>

                  {/* Price row — same vertical position on every card */}
                  <div className="text-center mb-1 mt-2"><span className="text-3xl md:text-4xl font-bold text-gray-900">{plan.price}</span><span className="text-gray-500 text-sm">{plan.period}</span></div>

                  {/* Division line between price and features */}
                  <hr className="border-t border-black my-3" />

                  <ul className="mb-5 flex-1" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {plan.features.map(f => <li key={f} className="flex items-start gap-2" style={{ fontSize: '13px', lineHeight: '1.55', color: '#444444' }}><Check size={14} className="text-green-500 flex-shrink-0 mt-0.5" />{f}</li>)}
                    {plan.excluded?.map(f => <li key={f} className="flex items-start gap-2" style={{ fontSize: '13px', lineHeight: '1.55', color: '#9CA3AF' }}><span className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0 mt-1" />{f}</li>)}
                  </ul>
                  <button onClick={() => navigate('/register')} className={`w-full rounded-xl font-bold ${plan.ctaStyle}`} style={{ minHeight: '48px', fontSize: '14px' }}>{plan.cta}</button>
                </div>
              ))}
              </div>
              <MeetTheAvAs />
            </>
          )}

          {/* Free-tier reassurance + 30-day refund + Cancel/Pause copy */}
          {pricingView === 'families' && (
            <>
              {/* Hearts section (Phase 1 spec) */}
              <div className="mt-10 max-w-3xl mx-auto text-center" data-testid="hearts-section">
                <h3 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Georgia, serif' }}>❤️ Hearts — extra voice practice</h3>
                <p className="text-sm text-gray-600 mb-6">Hearts work with any plan. Never run out of voice practice.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { hearts: 'Spark', price: '£5.99', mins: '60 minutes of voice' },
                    { hearts: 'Growth', price: '£17.99', mins: '240 minutes of voice', best: true },
                    { hearts: 'Champion', price: '£35.99', mins: '600 minutes of voice' },
                  ].map(p => (
                    <div key={p.hearts} className={`p-5 bg-white rounded-2xl border-2 ${p.best ? 'border-rose-300 shadow-md' : 'border-gray-200'}`} data-testid={`hearts-pack-${p.hearts.split(' ')[0]}`}>
                      <p className="text-sm font-bold text-rose-500">{p.hearts}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{p.price}</p>
                      <p className="text-xs text-gray-500 mt-1">{p.mins}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Full plan comparison grid (Phase 1 spec) */}
              <PlanComparisonTable />
            </>
          )}

          {/* Adults (18+) pricing — professional-growth tiers */}
          {pricingView === 'adults' && (
            <>
              <div className="-mt-6 mb-10 max-w-md mx-auto px-4 py-3 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border border-amber-200 rounded-xl text-center" data-testid="adult-tier-banner">
                <p className="text-sm font-bold text-gray-900 inline-flex items-center justify-center gap-1.5">
                  <Sparkles size={14} style={{ color: '#F5831F' }} />
                  Built for adults who practise the conversations that move careers.
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Interview prep, salary negotiations, dating, boundary-setting, public speaking — same AvA engine, adult-only scenarios.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4" style={{ gap: '20px' }}>
              {[
                { name: 'ENTRY',     tagline: 'Try Your First Rep',          price: '$0',   period: '/forever', badgeLabel: 'ENTRY',     badgeStyle: 'bg-emerald-100 text-emerald-700 border border-emerald-300', features: ['Maya — Your Guide', '3 starter scenarios', '15 FREE practice minutes', 'Adult-only safety guardrails'],                          excluded: ['Custom practice AvA', 'Voice messaging', 'Career playbook library'], cta: 'Start Free — No Card',     ctaStyle: 'bg-emerald-600 text-white hover:bg-emerald-700', free: true },
                { name: 'PRO',       tagline: 'Practise to Win',             price: '$19',  period: '/mo',     badgeLabel: 'PRO',       badgeStyle: 'bg-amber-100 text-amber-700 border border-amber-300',     features: ['1 personalised practice AvA', 'Full career playbook library', 'Unlimited text messages', '45 voice minutes / month', 'Interview & negotiation packs'], excluded: ['Multiple personas', 'Advanced replay analytics'], cta: 'Upgrade to Pro',           ctaStyle: 'bg-amber-50 text-amber-700 border-2 border-amber-500 hover:bg-amber-100' },
                { name: 'EXECUTIVE', tagline: 'Boardroom-Ready Confidence',  price: '$49',  period: '/mo',     badgeLabel: 'EXECUTIVE', badgeStyle: 'bg-orange-500 text-white',                                   features: ['3 practice AvAs (work / dating / family)', 'Every scenario unlocked', 'Unlimited text', '180 voice minutes / month', 'Replay analytics + tone scoring', 'Stripe invoiced billing'],                                                                                                                                                                                                                                                                                                                                                                                       cta: 'Upgrade to Executive',     ctaStyle: 'bg-orange-500 text-white hover:bg-orange-600', popular: true },
                { name: 'MASTER',    tagline: 'Annual — Save 33%',           price: '$390', period: '/year',   sub: 'Only $32.50/mo billed annually', badgeLabel: 'MASTER ANNUAL', badgeStyle: 'bg-[#7B4FD8] text-white',                                features: ['5 practice AvAs', 'Everything in Executive', '360 voice minutes / month', 'Priority human coach review', 'Same 30-day refund + pause options'],                                                                                                                                                                                                                                                                                                                                                                                                                       cta: 'Upgrade to Master Annual', ctaStyle: 'bg-[#7B4FD8] text-white hover:bg-[#6A3FC7]', annual: true },
              ].map(plan => (
                <div key={plan.name} className={`p-5 md:p-6 bg-white rounded-2xl border-2 ${plan.free ? 'border-emerald-400 shadow-lg' : plan.popular ? 'border-orange-400 shadow-lg' : plan.annual ? 'border-purple-300' : 'border-gray-200'} relative flex flex-col`} data-testid={`adult-pricing-card-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <span className={`absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 text-sm font-extrabold tracking-[0.18em] rounded-full whitespace-nowrap shadow-sm ${plan.badgeStyle}`}>{plan.badgeLabel}</span>
                  <h3 className="font-bold text-gray-800 mt-4 text-center text-lg leading-snug min-h-[3.25rem] flex items-center justify-center px-2" style={{ fontFamily: 'Georgia, serif' }}>{plan.tagline}</h3>
                  <div className="text-center mb-1 mt-2"><span className="text-3xl md:text-4xl font-bold text-gray-900">{plan.price}</span><span className="text-gray-500 text-sm">{plan.period}</span></div>
                  <p className="text-xs font-medium italic text-center min-h-[1.25rem]" style={{ color: '#F5831F' }}>
                    {plan.sub || '\u00A0'}
                  </p>
                  <p className="text-[11px] font-semibold text-center min-h-[2.75rem] flex items-center justify-center gap-1" style={{ color: 'transparent' }}>{'\u00A0'}</p>
                  <ul className="mb-5 mt-3 flex-1" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {plan.features.map(f => <li key={f} className="flex items-start gap-2" style={{ fontSize: '13px', lineHeight: '1.55', color: '#444444' }}><Check size={14} className="text-green-500 flex-shrink-0 mt-0.5" />{f}</li>)}
                    {plan.excluded?.map(f => <li key={f} className="flex items-start gap-2" style={{ fontSize: '13px', lineHeight: '1.55', color: '#9CA3AF' }}><span className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0 mt-1" />{f}</li>)}
                  </ul>
                  <button onClick={() => { setAudiencePersona('adult'); navigate('/adult'); }} className={`w-full rounded-xl font-bold ${plan.ctaStyle}`} style={{ minHeight: '48px', fontSize: '14px' }}>{plan.cta}</button>
                </div>
              ))}
              </div>
              <p className="text-center text-sm text-gray-600 mt-8 max-w-2xl mx-auto" data-testid="adult-pricing-guarantee">
                <strong className="text-gray-900">Free forever.</strong> No card needed to start.
                All paid plans include a <strong>30-day full refund</strong>, plus a <strong>90-day pause</strong> (no billing) — your scenarios, replays and progress are preserved.
              </p>
            </>
          )}

          {/* Schools pricing */}
          {pricingView === 'schools' && (
            <div className="max-w-3xl mx-auto text-center" data-testid="schools-pricing-section">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('price.schoolsH')}</h3>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">{t('schools.onerate')}</p>
              <div className="p-8 bg-white rounded-2xl border-2 border-[#7B4FD8] mb-6" data-testid="schools-single-rate-card">
                <p className="text-5xl font-bold text-gray-900">$6.85<span className="text-base font-normal text-gray-500"> /student/month</span></p>
                <p className="text-sm text-gray-600 mt-3">{t('price.schoolsMin')}</p>
                <ul className="text-sm text-gray-600 mt-5 space-y-1.5 text-left max-w-md mx-auto">
                  <li>• Full access to all 9 specialist AvA tutors — text and voice</li>
                  <li>• School dashboards with anonymised insights</li>
                  <li>• Complete safety stack (NVIDIA NeMo, custom Claude Sonnet 4.5, upgraded COPPA)</li>
                  <li>• Parent portal, ongoing updates and support</li>
                </ul>
              </div>
              <div className="text-center">
                <button onClick={() => window.location.href = '/school/register'} className="px-8 py-3 bg-[#7B4FD8] text-white font-semibold rounded-xl hover:bg-[#6A3FC7] text-base" data-testid="schools-start-beta-btn">{t('price.schoolsCta')}</button>
                <p className="text-sm text-gray-500 mt-3">Questions first? <a href="mailto:schools@hibff.com" className="text-purple-600 underline">Book a school demo — schools@hibff.com</a></p>
                <p className="text-sm mt-2"><a href="/education" className="text-purple-600 font-semibold underline" data-testid="schools-full-details-link">Full details for schools &amp; districts →</a></p>
              </div>
            </div>
          )}

          {/* Businesses pricing */}
          {pricingView === 'businesses' && (
            <div className="max-w-lg mx-auto p-8 bg-white rounded-2xl border-2 border-green-300 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('price.bizH')}</h3>
              <p className="text-gray-600 mb-6">{t('price.bizP')}</p>
              <button onClick={() => window.location.href = 'mailto:business@hibff.com'} className="px-8 py-3 bg-[#2EAA5C] text-white font-semibold rounded-xl hover:bg-[#259A50] text-base">{t('price.bizCta')}</button>
              <p className="text-sm text-gray-500 mt-3">Or email us at <a href="mailto:business@hibff.com" className="text-purple-600 underline">business@hibff.com</a></p>
            </div>
          )}
        </div>
      </section>

      {/* Testimonial — after pricing (Phase 1 spec) */}
      <Testimonial
        id="mark"
        quote="The safety features are what sold me. I know my son's conversations are private — even from me. That trust matters. He actually opens up to Neo in ways he never would with me. And I get to see his progress without invading his space."
        name="Mark"
        role="Parent of a 14-year-old"
      />

      {/* Testimonial — footer area (Phase 1 spec) */}
      <div className="border-t" style={{ borderColor: '#F0F0F0' }}>
        <Testimonial
          id="jordan"
          quote="My parents don't read my conversations. That's the best part. I can be honest with Neo. And Neo helps me be honest with myself."
          name="Jordan"
          role="14"
          accent="#7B4FD8"
        />
      </div>

      {/* ── Section 14: Footer (5 columns per Phase 1 spec) ── */}
      <footer className="py-8 px-4 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-6">
            <div>
              <h4 className="font-bold mb-3">{t('foot.product')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/register?persona=teen" className="hover:text-white" data-testid="footer-product-teens">Teens</a></li>
                <li><a href="/parent-first" className="hover:text-white" data-testid="footer-product-parents">Parents</a></li>
                <li><a href="/adult" className="hover:text-white" data-testid="footer-product-adults">Adults</a></li>
                <li><a href="/education" className="hover:text-white" data-testid="footer-product-schools">Education</a></li>
                <li><a href="/pricing" className="hover:text-white" data-testid="footer-product-pricing">Pricing</a></li>
                <li><a href="/corporate/register" className="hover:text-white" data-testid="footer-product-corporates">Corporates</a></li>
                <li><a href="/contact" className="hover:text-white" data-testid="footer-product-government">Government Agencies</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">{t('foot.company')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/about" className="hover:text-white">About</a></li>
                <li><a href="/investor-portal" className="hover:text-white" data-testid="footer-investors-link">Investor Portal</a></li>
                <li><button onClick={() => setShowNewsletterModal(true)} className="hover:text-white text-left" data-testid="footer-newsletter-btn">Newsletter</button></li>
                <li><a href="/partners" className="hover:text-white">Partners</a></li>
                <li><a href="/warm-handover?src=footer" target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="footer-company-whitepaper">Warm Handover White Paper</a></li>
                <li><a href="mailto:careers@hibff.com" className="hover:text-white" data-testid="footer-company-careers">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">{t('foot.parents')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => setShowCharterModal(true)} className="hover:text-white text-left">Preview the Charter</button></li>
                <li><a href="/teen-safety" className="hover:text-white">Teen Safety & Privacy</a></li>
                <li><a href="/trust-safety" className="hover:text-white">Trust & Safety — Live Numbers</a></li>
                <li><a href="/docs" className="hover:text-white">Parent Dashboard Guide</a></li>
                <li><a href="/weekly-report" className="hover:text-white">Monthly Report — How It Works</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">{t('foot.legal')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/terms" className="hover:text-white" data-testid="footer-terms-link">Terms &amp; Conditions</a></li>
                <li><a href="/privacy" className="hover:text-white" data-testid="footer-privacy-link">Privacy &amp; Data Safety Policy</a></li>
                <li><a href="/refund-policy" className="hover:text-white" data-testid="footer-refund-link">Refund Policy</a></li>
                <li><a href="/pause-policy-90" className="hover:text-white" data-testid="footer-pause90-link">90 Day Pause Policy</a></li>
                <li><a href="/pause-policy-indefinite" className="hover:text-white" data-testid="footer-pause-indef-link">Indefinite Pause Policy</a></li></ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">{t('foot.contact')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="mailto:parents@hibff.com" className="hover:text-white">parents@hibff.com</a></li>
                <li><a href="mailto:schools@hibff.com" className="hover:text-white">schools@hibff.com</a></li>
                <li><a href="mailto:business@hibff.com" className="hover:text-white">business@hibff.com</a></li>
                <li><a href="mailto:support@hibff.com" className="hover:text-white">support@hibff.com</a></li>
                <li>
                  <a href="https://play.google.com/store/apps/details?id=com.hibff.coach" target="_blank" rel="noopener noreferrer" aria-label="Get HiBFF on Google Play" data-testid="footer-google-play-badge" className="inline-block mt-2">
                    <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" className="h-12 w-auto" loading="lazy" />
                  </a>
                </li></ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-4 text-center">
            <p className="text-xs text-gray-500">HiBFF is protected by 3 live UK patent applications (GB2617027.4 &middot; GB2622892.4 &middot; GB2622997.1), all filed at the UK IPO. &copy; HiBFF Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Lead-capture gate — visitors must submit name + email + role
          before they can read, open, or download the Welcome Pack. */}
      {GateModal}

      {/* Early-access modal — opens from the top banner and the
          "Reserve 90 days free" badge inside the Welcome Pack section. */}
      <EarlyAccessModal
        isOpen={showEarlyAccess}
        onClose={() => setShowEarlyAccess(false)}
      />

      {/* Welcome Pack inline reader — opens when "Read on screen" is clicked.
          Uses an iframe to render the PDF natively in the browser, with tabs
          for both documents and a clear "Download" CTA in the header. */}
      <NewsletterModal open={showNewsletterModal} onClose={() => setShowNewsletterModal(false)} />

      {showCharterModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" data-testid="welcome-pack-reader">
          <div className="absolute inset-0 bg-[#1A1209]/70 backdrop-blur-sm" onClick={() => setShowCharterModal(false)} />
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FFF5EA' }}>
                  <FileText size={18} style={{ color: '#F5831F' }} />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#F5831F' }}>The HiBFF Welcome Pack</p>
                  <h3 className="text-base font-bold leading-tight" style={{ color: '#1A1209' }}>
                    {showCharterModal === 'teen' ? 'Teen Digital Compact' : 'Parent Compact'}
                  </h3>
                </div>
              </div>
              <button
                onClick={() => setShowCharterModal(false)}
                className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
                aria-label="Close"
                data-testid="welcome-pack-reader-close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 px-5 sm:px-6 py-3 border-b border-gray-100 bg-gray-50">
              <button
                onClick={() => setShowCharterModal('parent')}
                className={`px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
                  showCharterModal === 'parent' ? 'bg-white text-[#1A1209] shadow-sm' : 'text-gray-500 hover:text-gray-900'
                }`}
                data-testid="welcome-pack-tab-parent"
              >
                Parent Compact
              </button>
              <button
                onClick={() => setShowCharterModal('teen')}
                className={`px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
                  showCharterModal === 'teen' ? 'bg-white text-[#1A1209] shadow-sm' : 'text-gray-500 hover:text-gray-900'
                }`}
                data-testid="welcome-pack-tab-teen"
              >
                Teen Digital Compact
              </button>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => requireUnlock(() => {
                    const url = showCharterModal === 'teen'
                      ? '/api/static/charters/HiBFF-Teen-Digital-Compact.pdf'
                      : '/api/static/charters/HiBFF-Parent-Compact.pdf';
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }, 'open')}
                  className="text-xs font-semibold text-[#7B4FD8] hover:text-[#5e36b1] inline-flex items-center gap-1.5 px-3 py-2"
                  data-testid="welcome-pack-reader-newtab"
                >
                  Open in new tab
                </button>
                <button
                  type="button"
                  onClick={() => requireUnlock(() => {
                    const url = showCharterModal === 'teen'
                      ? '/api/static/charters/HiBFF-Teen-Digital-Compact.pdf'
                      : '/api/static/charters/HiBFF-Parent-Compact.pdf';
                    const name = showCharterModal === 'teen' ? 'HiBFF Teen Digital Compact.pdf' : 'HiBFF Parent Compact.pdf';
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = name;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }, 'download')}
                  className="text-xs font-semibold text-[#F5831F] hover:text-[#E07318] inline-flex items-center gap-1.5 px-3 py-2"
                  data-testid="welcome-pack-reader-download"
                >
                  <Download size={12} /> Download this one
                </button>
              </div>
            </div>

            {/* Body — rendered as plain <img> pages so it Just Works on
                every browser, mobile Safari, and in-app webview. PDF
                downloads remain available via the Open / Download buttons
                above. */}
            <WelcomePackImageReader
              images={showCharterModal === 'teen' ? WELCOME_PACK_IMAGES.teen : WELCOME_PACK_IMAGES.parent}
              label={showCharterModal === 'teen' ? 'Teen Digital Compact' : 'Parent Compact'}
            />

            {/* Footer */}
            <div className="flex items-center justify-between flex-wrap gap-3 px-5 sm:px-6 py-3 border-t border-gray-100 bg-white">
              <p className="text-xs italic" style={{ color: '#5C4A33' }}>
                Both documents are signed digitally through your Parent Dashboard once your account is verified.
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowCharterModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-28 sm:bottom-8 right-6 sm:right-8 z-[60] w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
        style={{ backgroundColor: '#7B4FD8', color: '#FFF' }}
        aria-label="Scroll to top"
        data-testid="scroll-to-top-btn"
      >
        <ArrowRight size={20} className="-rotate-90" />
      </button>
    </div>
  );
};

export default Homepage;
